import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import { Input } from "reactstrap";
import SimpleHeader from "components/Headers/SimpleHeader";
import { getDeviceProfile, createDeviceProfile, updateDeviceProfile } from "./deviceService";
import { deviceProfileForm } from "../configs/Forms/AccountsForms";
import { serverHost } from "variables/serverdata";
import { getAuthToken } from "../util/helpers";

const titleCase = (s) =>
  s.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

const DeviceProfileAddEdit = () => {
  const { device_profile_uuid } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(device_profile_uuid);

  const [profile, setProfile] = useState({});
  const [keys, setKeys] = useState([]);
  const [settings, setSettings] = useState([]);
  const [domains, setDomains] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(editing);
  const [error, setError] = useState("");

  const setterMap = { Keys: setKeys, Settings: setSettings };
  const arrayMap = { Keys: keys, Settings: settings };

  // Fetch domains
  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;
        const res = await fetch(
          `${serverHost}/get_rows?table=v_domains&page=1&pageSize=1000`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        if (Array.isArray(data.results)) setDomains(data.results);
      } catch (err) {
        console.error("Error fetching domains:", err);
      }
    };
    fetchDomains();
  }, []);

  // Fetch vendors
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;
        const res = await fetch(
          `${serverHost}/get_rows?table=v_device_vendors&page=1&pageSize=1000`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        if (Array.isArray(data.results)) setVendors(data.results);
      } catch (err) {
        console.error("Error fetching vendors:", err);
      }
    };
    fetchVendors();
  }, []);

  // Fetch profile if editing
  useEffect(() => {
    if (!editing) return;
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const data = await getDeviceProfile(device_profile_uuid);
        if (data.error) {
          setError(data.error);
          return;
        }
        setProfile(data.profile || {});
        setKeys(data.keys || []);
        setSettings(data.settings || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [device_profile_uuid, editing]);

  const handleChange = (field, value) => setProfile(prev => ({ ...prev, [field]: value }));
  const handleArrayChange = (setter, index, field, value, array) => {
    const updated = [...array];
    updated[index] = { ...updated[index], [field]: value };
    setter(updated);
  };
  const handleAddItem = (setter, emptyTemplate, array) => setter([...array, { ...emptyTemplate }]);

  const handleDeleteItem = async (section, item) => {
    const token = getAuthToken();
    if (!token) return;

    const sectionMap = {
      Keys: { type: "key", uuidField: "profile_key_uuid" },
      Settings: { type: "setting", uuidField: "profile_setting_uuid" },
    };

    const { type, uuidField } = sectionMap[section];
    const itemUuid = item[uuidField];

    const setter = setterMap[section];
    const array = arrayMap[section];

    if (!itemUuid) {
      setter(array.filter(i => i !== item));
      return;
    }

    try {
      const res = await fetch(`${serverHost}/delete_device_profile_item/${type}/${itemUuid}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();

      if (res.ok) setter(array.filter(i => i[uuidField] !== itemUuid));
      else alert(`Failed to delete: ${data.error}`);
    } catch (err) {
      console.error(err);
      alert(`Error deleting ${type}`);
    }
  };

  const handleSave = async () => {
    setError("");
    const missing = [];
    const user = JSON.parse(localStorage.getItem("user"));
    const user_uuid = user?.user_uuid || null;
    const domain = JSON.parse(localStorage.getItem("selectedSite"));
    const domain_uuid = domain?.site_uuid || null;

    deviceProfileForm.required.forEach(field => {
      if (!profile[field]) missing.push(field);
    });

    Object.entries(deviceProfileForm.requiredSections || {}).forEach(([section, fields]) => {
      const items = { Keys: keys, Settings: settings }[section] || [];
      items.forEach((item, idx) => {
        fields.forEach(f => {
          if (!item[f]) missing.push(`${section}[${idx}].${f}`);
        });
      });
    });

    if (missing.length) {
      setError(`Required fields missing: ${missing.join(", ")}`);
      return;
    }

    try {
      const payload = { profile, keys, settings };

      if (editing) await updateDeviceProfile(device_profile_uuid, payload, domain_uuid, user_uuid);
      else await createDeviceProfile(payload, domain_uuid, user_uuid);

      navigate("/admin/hucs/deviceProfiles");
    } catch (err) {
      setError(err.message || "Failed to save profile");
    }
  };

  const dynamicProfileSelectOptions = {
    profile_key_vendor: vendors.map(v => ({
      label: v.name,
      value: v.name,
    })),
    domain_uuid: domains.map(d => ({
      label: d.domain_name,
      value: d.domain_uuid,
    })),
  };

  const renderField = (section, field, value, onChange) => {
    const type = deviceProfileForm.types[field];
    const options = deviceProfileForm.options?.[field] || [];
    const defaultValue = deviceProfileForm.defaults?.[field];
    const effectiveValue = value ?? defaultValue ?? "";
    const label =
      deviceProfileForm.labels?.[field] || titleCase(field);

    const placeholder = value
      ? ""
      : `Enter ${label.toLowerCase()}`;

    // BOOLEAN
    if (type === "boolean") {
      return (
        <Input
          type="select"
          value={effectiveValue}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="true">True</option>
          <option value="false">False</option>
        </Input>
      );
    }

    // SELECT
    if (type === "select") {
      return (
        <Input
          type="select"
          value={effectiveValue}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">
            Select {deviceProfileForm.labels[field] || field}
          </option>

          {options.map((opt) =>
            typeof opt === "object" ? (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ) : (
              <option key={opt} value={opt}>
                {opt}
              </option>
            )
          )}
        </Input>
      );
    }
    // DEFAULT (text / number / etc)
    return (
      <Input
        type="text"
        value={effectiveValue}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    );
  };

  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
        <p className="mt-2">Loading profile...</p>
      </div>
    );

  return (
    <>
      <SimpleHeader name={`${editing ? "Edit" : "Add"} Device Profile`} parentName="Device Profiles" />
      <Container className="mt--6">
        <Card className="shadow-sm" style={{ marginTop: "-3rem" }}>
          <Card.Header className="header-theme">
            <h4 className="mb-0">{editing ? "EDIT PROFILE" : "ADD PROFILE"}</h4>
          </Card.Header>
          <Card.Body className="p-4">
            {error && <Alert variant="danger">{error}</Alert>}
            <Form>
              {Object.entries(deviceProfileForm.sections).map(([sectionName, fields]) => {
                const setter = setterMap[sectionName];
                const items = arrayMap[sectionName];

                return (
                  <div key={sectionName}>
                    <h5 className="section-title mb-3 mt-4" style={{ fontSize: "1.5rem", fontWeight: 700 }}>
                      {sectionName}
                    </h5>

                    {["Keys", "Settings"].includes(sectionName) &&
                      items?.map((item, idx) => (
                        <Row key={idx} className="mb-2">
                          {fields.map((field, i) => (
                            <Col md={3} key={i}>
                              <Form.Group className="mb-3">
                                <Form.Label>
                                  {deviceProfileForm.labels[field] || field}
                                  {(deviceProfileForm.requiredSections?.[sectionName] || []).includes(field) && (
                                    <span style={{ color: "red" }}> *</span>
                                  )}
                                </Form.Label>

                                {renderField(
                                  sectionName,
                                  field,
                                  item[field] ?? "",
                                  value => handleArrayChange(setter, idx, field, value, items),
                                  dynamicProfileSelectOptions
                                )}
                              </Form.Group>
                            </Col>
                          ))}

                          <Col md={2}>
                            <Button
                              variant="danger"
                              onClick={() => handleDeleteItem(sectionName, item)}
                            >
                              Delete
                            </Button>
                          </Col>
                        </Row>
                      ))}

                    {["Keys", "Settings"].includes(sectionName) && (
                      <Button
                        className="mt-2"
                        onClick={() => {
                          const emptyTemplate = {};
                          fields.forEach(f => {
                            emptyTemplate[f] = deviceProfileForm.defaults?.[f] ?? "";
                          });
                          handleAddItem(setter, emptyTemplate, items);
                        }}
                      >
                        Add {sectionName.slice(0, -1)}
                      </Button>
                    )}

                    {sectionName === "Basic Info" && (
                      <Row className="mb-2">
                        {fields.map((field, i) => (
                          <Col md={4} key={i}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                {deviceProfileForm.labels[field] || field}
                                {(deviceProfileForm.required || []).includes(field) && (
                                  <span style={{ color: "red" }}> *</span>
                                )}
                              </Form.Label>

                              {renderField(
                                sectionName,
                                field,
                                profile[field] ?? "",
                                value => handleChange(field, value),
                                dynamicProfileSelectOptions
                              )}
                            </Form.Group>
                          </Col>
                        ))}
                      </Row>
                    )}
                  </div>
                );
              })}

              <div className="mt-4 d-flex justify-content-between">
                <Button variant="danger" onClick={() => navigate("/admin/hucs/deviceProfiles")}>Cancel</Button>
                <Button variant="success" onClick={handleSave}>{"Save"}</Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default DeviceProfileAddEdit;
